import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

/* ═══════════════════════════════════════════════════════════════════════════
   PIPELINE RING — Animated deal pipeline completion ring
   ═══════════════════════════════════════════════════════════════════════════ */
function PipelineRing({ targetScore = 78, animate = false }: { targetScore?: number; animate?: boolean }) {
  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    if (!animate) return;
    let frame: number;
    const duration = 2500;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(eased * targetScore));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animate, targetScore]);
  const circumference = 2 * Math.PI * 90;
  return (
    <div className="pipeline-ring" style={{ '--score': animate ? targetScore : 0 } as React.CSSProperties}>
      <svg width="220" height="220" viewBox="0 0 200 200">
        <defs>
          <linearGradient id="crmGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#60a5fa" />
            <stop offset="100%" stopColor="#93c5fd" />
          </linearGradient>
        </defs>
        <circle className="ring-track" cx="100" cy="100" r="90" />
        <circle className="ring-fill" cx="100" cy="100" r="90"
          style={{ strokeDasharray: circumference, strokeDashoffset: animate ? circumference - (circumference * targetScore) / 100 : circumference, transition: 'stroke-dashoffset 2.5s cubic-bezier(0.4, 0, 0.2, 1)' }} />
      </svg>
      <div className="pipeline-ring-value">
        <div className="stat-counter" style={{ fontSize: '3rem', fontWeight: 800, color: '#fff' }}>{displayScore}%</div>
        <div style={{ fontSize: '0.85rem', color: 'rgba(96, 165, 250, 0.7)', fontWeight: 500, marginTop: 4 }}>Win Rate</div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   AI LEAD SCORING DEMO — Interactive demo showing lead intelligence
   ═══════════════════════════════════════════════════════════════════════════ */
function LeadScoringDemo() {
  const [phase, setPhase] = useState<'idle' | 'scanning' | 'scored'>('idle');
  const [visibleLeads, setVisibleLeads] = useState<number[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  const leads = [
    { name: 'Apex Construction Ltd', score: 94, status: '🔥 Hot', industry: 'Construction', value: '£48,000', signal: 'Visited pricing 3x this week' },
    { name: 'Thames Valley Property', score: 72, status: '🌡️ Warm', industry: 'Real Estate', value: '£24,000', signal: 'Opened last 5 emails' },
    { name: 'Northern Digital Agency', score: 45, status: '❄️ Cold', industry: 'Digital', value: '£12,000', signal: 'No activity in 14 days' },
    { name: 'Midlands Logistics Group', score: 88, status: '🔥 Hot', industry: 'Logistics', value: '£65,000', signal: 'Requested demo via form' },
  ];

  const startDemo = useCallback(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    setPhase('scanning');
    setTimeout(() => {
      setPhase('scored');
      leads.forEach((_, idx) => {
        setTimeout(() => setVisibleLeads(prev => [...prev, idx]), idx * 400);
      });
    }, 1200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTimeout(startDemo, 400); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startDemo]);

  return (
    <div ref={ref} style={{ width: '100%', maxWidth: 700, margin: '0 auto' }}>
      <div className="glass-card" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 12, borderBottom: '1px solid rgba(59,130,246,0.15)' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#60a5fa', animation: 'blink 2s ease-in-out infinite' }} />
          <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 500 }}>AI Lead Intelligence — Live Demo</span>
        </div>

        {phase === 'scanning' && (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.2rem', color: '#60a5fa', fontWeight: 600 }}>🔍 Scanning pipeline...</div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 8 }}>AI is analysing engagement signals, email opens, and website activity</div>
          </div>
        )}

        {phase === 'scored' && (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {leads.map((lead, idx) => (
              visibleLeads.includes(idx) && (
                <div key={lead.name} className="pipeline-stage" style={{ animationDelay: `${idx * 0.1}s` }}>
                  <div className="glass-card" style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: lead.score >= 80 ? 'rgba(239,68,68,0.15)' : lead.score >= 60 ? 'rgba(245,158,11,0.15)' : 'rgba(100,116,139,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', fontWeight: 800, color: lead.score >= 80 ? '#f87171' : lead.score >= 60 ? '#fbbf24' : '#94a3b8', flexShrink: 0 }}>
                      {lead.score}
                    </div>
                    <div style={{ flex: 1, minWidth: 150 }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff' }}>{lead.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>{lead.industry} · {lead.value}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{lead.status}</div>
                      <div style={{ fontSize: '0.7rem', color: '#60a5fa', marginTop: 4 }}>{lead.signal}</div>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PIPELINE VISUALIZATION — Animated deal stage funnel
   ═══════════════════════════════════════════════════════════════════════════ */
function PipelineDemo() {
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const stages = [
    { name: 'Qualified', count: 24, value: '£340K', width: 100, color: '#3b82f6' },
    { name: 'Proposal', count: 16, value: '£280K', width: 75, color: '#60a5fa' },
    { name: 'Negotiation', count: 8, value: '£190K', width: 50, color: '#93c5fd' },
    { name: 'Closed Won', count: 5, value: '£140K', width: 30, color: '#10b981' },
  ];

  return (
    <div ref={ref} style={{ width: '100%', maxWidth: 700, margin: '0 auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {stages.map((stage, i) => (
          <div key={stage.name}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#e2e8f0' }}>{stage.name}</span>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{stage.count} deals · {stage.value}</span>
            </div>
            <div style={{ height: 12, background: 'rgba(30, 37, 86, 0.6)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: inView ? `${stage.width}%` : '0%',
                background: stage.color,
                borderRadius: 999,
                transition: `width 1.5s cubic-bezier(0.4, 0, 0.2, 1) ${i * 0.3}s`,
              }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STATS COUNTER
   ═══════════════════════════════════════════════════════════════════════════ */
function StatsCounter({ end, suffix = '' }: { end: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!inView) return;
    let frame: number;
    const duration = 2000, start = performance.now();
    const tick = (now: number) => { const p = Math.min((now - start) / duration, 1); setCount(Math.round((1 - Math.pow(1 - p, 3)) * end)); if (p < 1) frame = requestAnimationFrame(tick); };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, end]);
  return <span ref={ref} className="stat-counter">{count.toLocaleString()}{suffix}</span>;
}

/* ═══════════════════════════════════════════════════════════════════════════ */
const FEATURES = [
  { icon: '🎯', title: 'AI Lead Scoring', description: 'Automatically score leads based on engagement, email opens, website visits, and firmographic data.', gradient: 'linear-gradient(135deg, #3b82f6, #60a5fa)' },
  { icon: '📧', title: 'AI Email Writing', description: 'Generate personalized follow-up emails, proposals, and outreach in your brand voice with one click.', gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' },
  { icon: '📊', title: 'Visual Pipeline', description: 'Drag-and-drop deal management with intelligent stage recommendations and risk alerts.', gradient: 'linear-gradient(135deg, #10b981, #34d399)' },
  { icon: '📝', title: 'Business Plan Generator', description: 'Generate professional business plans and tender submissions from your CRM data in minutes.', gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)' },
  { icon: '🏢', title: 'Companies House Lookup', description: 'Auto-fill company data, verify directors, and track filings directly from Companies House API.', gradient: 'linear-gradient(135deg, #ef4444, #f87171)' },
  { icon: '🤝', title: 'Human-In-The-Loop', description: 'AI suggests actions. You approve. Nothing is sent to a customer without your explicit sign-off.', gradient: 'linear-gradient(135deg, #ec4899, #f472b6)' },
];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN LANDING PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
export default function EFCRMLanding() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [heroInView, setHeroInView] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setHeroInView(true); }, { threshold: 0.1 });
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-root">
      {/* ─── NAV ─── */}
      <nav className={scrollY > 50 ? 'nav-glass' : ''} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, transition: 'all 0.3s ease', ...(scrollY <= 50 ? { background: 'transparent' } : {}) }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #3b82f6, #60a5fa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>👥</div>
            <div><span style={{ fontSize: '1.1rem', fontWeight: 700 }}>EF-CRM</span><span style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: 8 }}>by Executive Force AI</span></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/login')} style={{ padding: '8px 16px', fontSize: '0.85rem', fontWeight: 500, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' }}>Sign In</button>
            <button onClick={() => navigate('/login')} style={{ padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600, color: '#fff', background: '#3b82f6', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Start Free Trial</button>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section ref={heroRef} className="grid-pattern" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5rem 1.5rem 3rem' }}>
        <div className="landing-orbs"><div className="landing-orb landing-orb--1" /><div className="landing-orb landing-orb--2" /><div className="landing-orb landing-orb--3" /></div>
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease 0.2s', marginBottom: 24 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(59,130,246,0.1)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)' }}>👥 AI-Native CRM</span>
          </div>
          <h1 style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 800, lineHeight: 1.1, marginBottom: 24, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease 0.4s' }}>
            <span style={{ color: '#fff' }}>Close More Deals</span><br />
            <span className="text-gradient-crm">Faster</span>
          </h1>
          <p style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)', color: '#94a3b8', maxWidth: 600, margin: '0 auto 3rem', lineHeight: 1.6, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease 0.6s' }}>
            AI lead scoring, pipeline intelligence, and email writing. Built for <span style={{ color: '#93c5fd', fontWeight: 600 }}>UK businesses</span>.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48, opacity: heroInView ? 1 : 0, transform: heroInView ? 'scale(1)' : 'scale(0.8)', transition: 'all 0.8s ease 0.8s' }}>
            <div className="animate-float"><PipelineRing targetScore={78} animate={heroInView} /></div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease 1.0s' }}>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>Start 7-Day Free Trial 🚀</button>
            <button onClick={() => navigate('/login')} style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: 500, color: '#94a3b8', background: 'transparent', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 12, cursor: 'pointer' }}>Sign In</button>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── AI LEAD SCORING DEMO ─── */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ display: 'inline-flex', padding: '6px 14px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(59,130,246,0.1)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)', marginBottom: 16 }}>AI Intelligence</span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>AI Scores Your <span className="text-gradient-crm">Leads Automatically</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>Every lead is scored in real-time based on engagement signals, email activity, and website behaviour. Focus on the deals that matter.</p>
          </div>
          <LeadScoringDemo />
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── PIPELINE VISUALIZATION ─── */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ display: 'inline-flex', padding: '6px 14px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(16,185,129,0.1)', color: '#34d399', border: '1px solid rgba(16,185,129,0.2)', marginBottom: 16 }}>Visual Pipeline</span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>See Your <span className="text-gradient-crm">Revenue Pipeline</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>Track every deal from qualification to close. AI flags at-risk deals and recommends next actions.</p>
          </div>
          <PipelineDemo />
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── FEATURES ─── */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Built for <span className="text-gradient-crm">UK Businesses</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>Companies House integration, VAT tracking, and UK-specific business features built in.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {FEATURES.map((f) => (
              <div key={f.title} className="glass-card" style={{ padding: '1.5rem' }}>
                <div className="feature-icon-glow"><div style={{ width: 48, height: 48, borderRadius: 12, background: f.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', marginBottom: 16 }}>{f.icon}</div></div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: '0.9rem', color: '#94a3b8', lineHeight: 1.6, margin: 0 }}>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── COMPETITOR COMPARISON ─── */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 48 }}>How EF-CRM <span className="text-gradient-crm">Compares</span></h2>
          <div className="glass-card" style={{ padding: '2rem', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(59,130,246,0.2)' }}>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>Feature</th>
                  <th style={{ padding: '12px 8px', textAlign: 'center', color: '#60a5fa', fontWeight: 700 }}>EF-CRM</th>
                  <th style={{ padding: '12px 8px', textAlign: 'center', color: '#64748b' }}>HubSpot</th>
                  <th style={{ padding: '12px 8px', textAlign: 'center', color: '#64748b' }}>Salesforce</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['AI-Native Architecture', '✅', '❌', '❌'],
                  ['UK Companies House', '✅', '❌', '❌'],
                  ['AI Email Writing', '✅', '💰', '💰'],
                  ['AI Lead Scoring', '✅', '💰', '💰'],
                  ['Price', '£29/mo', 'From £90/mo', 'From £25/user/mo'],
                ].map(([feature, ...values]) => (
                  <tr key={feature} style={{ borderBottom: '1px solid rgba(59,130,246,0.1)' }}>
                    <td style={{ padding: '12px 8px', color: '#e2e8f0', textAlign: 'left' }}>{feature}</td>
                    {values.map((v, i) => <td key={i} style={{ padding: '12px 8px', textAlign: 'center', color: i === 0 ? '#fff' : '#94a3b8' }}>{v}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── STATS ─── */}
      <section style={{ position: 'relative', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 32, textAlign: 'center' }}>
          {[
            { value: 78, suffix: '%', label: 'Avg Win Rate' },
            { value: 340, suffix: 'K', label: 'Pipeline Tracked' },
            { value: 50, suffix: '%', label: 'Time Saved' },
            { value: 99, suffix: '.9%', label: 'Uptime' },
          ].map((s) => (
            <div key={s.label}>
              <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#fff', marginBottom: 4 }}><StatsCounter end={s.value} suffix={s.suffix} /></div>
              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── PRICING ─── */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Simple <span className="text-gradient-crm">Pricing</span></h2>
          <p style={{ fontSize: '1.1rem', color: '#94a3b8', marginBottom: 40 }}>7-day free trial. No credit card required. Cancel anytime.</p>
          <div className="glass-card" style={{ padding: '2.5rem', borderColor: 'rgba(59,130,246,0.4)' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>EF-CRM</div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
              <span style={{ fontSize: '3.5rem', fontWeight: 800, color: '#fff' }}>£29</span>
              <span style={{ fontSize: '1.1rem', color: '#64748b' }}>/month</span>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: 24 }}>AI lead scoring · Pipeline management · Email writing</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', textAlign: 'left' }}>
              {['AI lead scoring & intelligence', 'Visual deal pipeline', 'AI email writing in your brand voice', 'Business plan generator', 'Companies House auto-fill', 'Cross-app data sync'].map((item) => (
                <li key={item} style={{ fontSize: '0.9rem', color: '#cbd5e1', padding: '6px 0', display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ color: '#60a5fa' }}>✓</span> {item}</li>
              ))}
            </ul>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ width: '100%', padding: '14px', fontSize: '1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start 7-Day Free Trial</button>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── FINAL CTA ─── */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div className="landing-orbs"><div className="landing-orb landing-orb--2" style={{ bottom: '20%', left: '10%' }} /><div className="landing-orb landing-orb--3" style={{ top: '10%', right: '10%' }} /></div>
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, marginBottom: 24 }}>Ready to <span className="text-gradient-crm">Close More Deals</span>?</h2>
          <p style={{ fontSize: '1.2rem', color: '#94a3b8', marginBottom: 40 }}>Stop losing leads to manual processes. Let AI handle the scoring while you close.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ padding: '16px 40px', fontSize: '1.1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start Free Trial →</button>
            <button onClick={() => navigate('/login')} style={{ padding: '16px 40px', fontSize: '1.1rem', fontWeight: 500, color: '#94a3b8', background: 'transparent', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 12, cursor: 'pointer' }}>Sign In</button>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer style={{ borderTop: '1px solid rgba(59,130,246,0.15)', padding: '2rem 1.5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: 'linear-gradient(135deg, #3b82f6, #60a5fa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem' }}>👥</div>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Executive Force AI</span>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#475569', margin: 0 }}>© {new Date().getFullYear()} Executive Force AI Limited. All rights reserved.</p>
          <div style={{ display: 'flex', gap: 24 }}>
            <button onClick={() => navigate('/login')} style={{ fontSize: '0.75rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>Sign In</button>
            <a href="https://executiveforceai.co.uk/privacy-policy" style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>Privacy</a>
            <a href="https://executiveforceai.co.uk/terms-conditions" style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
